import { useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';

import { useContainerWidth } from '~hooks/useContainerWidth';
import ArrowBackIcon from '~public/assets/icons/arrowBack.svg';
import { ButtonIconGhost } from '~shared/ui/atoms/Buttons/ButtonGhost/Icon';

import { Button, StyledEndlessCarousel, Wrapper } from './styles';

export const EndlessCarousel = <T extends { key: string }>({
	array,
	template,
	selected,
	gap = 16,
	onChange,
	className,
}: {
	array: T[];
	gap?: number;
	selected: (item: T) => boolean;
	template: (item: T, selected: boolean, index: number) => React.ReactNode;
	onChange: (item: T) => void;
	className?: string;
}) => {
	const [leftItems, setLeftItems] = useState(0);
	const [itemWidth, setItemWidth] = useState(0);
	const [isCarousel, setIsCarousel] = useState(false);
	const [isCarouselInit, setIsCarouselInit] = useState(false);
	const carouselRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);
	const containerWidth = useContainerWidth(carouselRef, 300);
	const [selectedIndex, setSelectedIndex] = useState(array.findIndex(selected));
	const [prevSelectedIndex, setPrevSelectedIndex] = useState(
		array.findIndex(selected)
	);

	const newSelectedIndex = array.findIndex(selected);
	if (newSelectedIndex !== selectedIndex && isCarouselInit) {
		setPrevSelectedIndex(selectedIndex);
		setSelectedIndex(newSelectedIndex);
	}

	const handleClick = (right: boolean) => {
		let index = selectedIndex + (right ? 1 : -1);
		if (index < 0) {
			index = array.length - 1;
		} else if (index >= array.length) {
			index = 0;
		}
		onChange(array[index]);
	};

	const handlers = useSwipeable({
		onSwiped: (eventData) =>
			eventData.dir === 'Right' || eventData.dir === 'Left'
				? handleClick(eventData.dir === 'Left')
				: undefined,
	});

	useEffect(() => {
		setIsCarousel(
			containerWidth !== 0 && array.length * (itemWidth + gap) > containerWidth
		);
	}, [itemWidth, containerWidth, array, gap]);

	useEffect(() => {
		if (!isCarousel) {
			setIsCarouselInit(false);
			setPrevSelectedIndex(0);
			setSelectedIndex(0);
			setLeftItems(0);
		}
	}, [isCarousel]);

	useEffect(() => {
		if (wrapperRef.current) {
			const itemWidth =
				wrapperRef.current.children[0].getBoundingClientRect().width;
			setItemWidth(itemWidth);
		}
	}, [array.length, gap, containerWidth]);

	useEffect(() => {
		if (!itemWidth || !wrapperRef.current) return;
		if (isCarousel) {
			if (!isCarouselInit) {
				if (selectedIndex > array.length / 2) {
					setLeftItems(selectedIndex);
				} else {
					setLeftItems(array.length + selectedIndex);
				}
				setIsCarouselInit(true);
			} else {
				const isNegative = prevSelectedIndex - selectedIndex < 0;
				const shift =
					Math.abs(prevSelectedIndex - selectedIndex) > array.length / 2
						? prevSelectedIndex -
						  selectedIndex +
						  (isNegative ? array.length : -array.length)
						: prevSelectedIndex - selectedIndex;
				setLeftItems((pred) => pred - shift);
			}
		} else {
			setLeftItems(0);
		}
	}, [
		array.length,
		isCarousel,
		isCarouselInit,
		itemWidth,
		prevSelectedIndex,
		selectedIndex,
	]);

	return (
		<StyledEndlessCarousel
			c={isCarousel}
			{...handlers}
			ref={(e) => {
				carouselRef.current = e;
				handlers.ref(e);
			}}
			className={className}
		>
			<Wrapper
				leftMargin={
					isCarousel
						? array.length *
						  (itemWidth + gap) *
						  Math.floor((leftItems - array.length / 2) / array.length)
						: 0
				}
				leftOffset={
					isCarousel
						? leftItems * (itemWidth + gap) - containerWidth / 2 + itemWidth / 2
						: 0
				}
				gap={gap}
				ref={wrapperRef}
			>
				{array.map((el, i) => (
					<button onClick={() => onChange(el)} key={el.key}>
						{template(el, newSelectedIndex === i, i)}
					</button>
				))}
				{isCarousel &&
					array.map((el, i) => (
						<button key={el.key} onClick={() => onChange(el)}>
							{template(el, selectedIndex === i, i)}
						</button>
					))}
			</Wrapper>
			{isCarousel &&
				Array.from({ length: 2 }).map((_, i) => (
					<Button onClick={() => handleClick(i > 0)} key={i} right={i > 0}>
						<ButtonIconGhost
							size="M"
							bigIcon={true}
							leftIcon={<ArrowBackIcon />}
						/>
					</Button>
				))}
		</StyledEndlessCarousel>
	);
};
